import { useState, useEffect } from "react";
import { assetMgrApi } from "@/api";
import type { CategoryTreeNode } from "@/types/asset-mgr";

// Module-level cache keyed by filter string (e.g. "" for unfiltered, "TOOL", "EQUIPMENT")
const treeCache = new Map<string, CategoryTreeNode[]>();
const fetchPromises = new Map<string, Promise<CategoryTreeNode[]>>();
// Mounted hooks, so an invalidation can make them refetch instead of sitting on
// the stale array they already rendered.
const subscribers = new Set<() => void>();

/**
 * Drop the cached tree and make every mounted useCategoryTree refetch.
 *
 * Call after ANY category write. The cache never expires on its own, so without
 * this a category edited in this session keeps serving its old row everywhere —
 * a renamed category still shows the old name in the pickers, and (the bug that
 * surfaced this) a category switched to auto-approve still resolves to "needs
 * approval" in the product Approval row until a full page reload.
 */
export function invalidateCategoryTree(): void {
    treeCache.clear();
    fetchPromises.clear();
    subscribers.forEach((notify) => notify());
}

interface UseCategoryTreeOptions {
    itemType?: string;
}

export function useCategoryTree(options?: UseCategoryTreeOptions) {
    const itemType = options?.itemType || "";
    const cacheKey = itemType;

    const [tree, setTree] = useState<CategoryTreeNode[]>(treeCache.get(cacheKey) || []);
    const [loading, setLoading] = useState(!treeCache.has(cacheKey));
    // Bumped by invalidateCategoryTree to re-run the fetch effect below. The
    // stale tree stays on screen until the new one lands (no empty flash).
    const [revision, setRevision] = useState(0);

    useEffect(() => {
        const bump = () => setRevision((r) => r + 1);
        subscribers.add(bump);
        return () => { subscribers.delete(bump); };
    }, []);

    useEffect(() => {
        const cached = treeCache.get(cacheKey);
        if (cached) {
            setTree(cached);
            setLoading(false);
            return;
        }

        if (!fetchPromises.has(cacheKey)) {
            const params = itemType ? { itemType } : undefined;
            const promise = assetMgrApi.getCategoryTree(params)
                .then((res: any) => {
                    const data = res?.data || res || [];
                    const result = Array.isArray(data) ? data : [];
                    treeCache.set(cacheKey, result);
                    fetchPromises.delete(cacheKey);
                    return result;
                })
                .catch(() => {
                    fetchPromises.delete(cacheKey);
                    return [] as CategoryTreeNode[];
                });
            fetchPromises.set(cacheKey, promise);
        }

        void fetchPromises.get(cacheKey)!.then((data) => {
            setTree(data);
            setLoading(false);
        });
    }, [cacheKey, itemType, revision]);

    return { tree, loading };
}

/** Recursively collect all category IDs in the subtree (including the node itself). */
export function collectAllIds(nodes: CategoryTreeNode[]): string[] {
    const ids: string[] = [];
    for (const node of nodes) {
        ids.push(node.id);
        if (node.children?.length) {
            ids.push(...collectAllIds(node.children));
        }
    }
    return ids;
}

/** Find a node by ID in the tree. */
export function findNodeById(nodes: CategoryTreeNode[], id: string): CategoryTreeNode | null {
    for (const node of nodes) {
        if (node.id === id) return node;
        if (node.children?.length) {
            const found = findNodeById(node.children, id);
            if (found) return found;
        }
    }
    return null;
}

/** Build breadcrumb path from root to target node. Returns array of node names. */
export function buildBreadcrumb(nodes: CategoryTreeNode[], targetId: string, path: string[] = []): string[] | null {
    for (const node of nodes) {
        if (node.id === targetId) return [...path, node.name];
        if (node.children?.length) {
            const found = buildBreadcrumb(node.children, targetId, [...path, node.name]);
            if (found) return found;
        }
    }
    return null;
}

/** Flatten tree nodes with breadcrumb for search display. */
export function flattenWithBreadcrumb(nodes: CategoryTreeNode[], ancestors: string[] = []): Array<{ node: CategoryTreeNode; breadcrumb: string }> {
    const result: Array<{ node: CategoryTreeNode; breadcrumb: string }> = [];
    for (const node of nodes) {
        const crumb = [...ancestors, node.name];
        result.push({ node, breadcrumb: crumb.join(" > ") });
        if (node.children?.length) {
            result.push(...flattenWithBreadcrumb(node.children, crumb));
        }
    }
    return result;
}

/**
 * Resolve the approval gate a product INHERITS when its own requiresApproval is
 * null. Mirrors the backend rule (product_service.resolve_requires_approval):
 * walk UP the category chain, first non-null ancestor wins; nothing set along
 * the way (or no category at all) falls back to true = needs approval.
 * Cycle/depth guarded, same as the backend.
 */
export function resolveInheritedApproval(
    nodes: CategoryTreeNode[],
    categoryId: string | null | undefined,
): boolean {
    let id = categoryId ? String(categoryId) : null;
    const seen = new Set<string>();
    let depth = 0;
    while (id && depth < 50) {
        if (seen.has(id)) break;
        seen.add(id);
        const node = findNodeById(nodes, id);
        if (!node) break;
        if (node.requiresApproval !== null && node.requiresApproval !== undefined) {
            return Boolean(node.requiresApproval);
        }
        id = node.parentCategoryId ? String(node.parentCategoryId) : null;
        depth += 1;
    }
    return true;
}
